import { z } from 'zod'

import { ChatMessageType } from '@/schema'

// ==================== Message Content Types ====================

export const messageContentSchema = z.object({
	id: z.string().optional(),
	order: z.number().default(0),
	messageType: z.nativeEnum(ChatMessageType),
	// TEXT
	text: z.string().optional(),
	// STICKER
	stickerPackageId: z.string().optional(),
	stickerId: z.string().optional(),
	// IMAGE
	imageUrl: z.string().optional(),
	// VIDEO
	videoUrl: z.string().optional(),
	videoPreviewUrl: z.string().optional(),
	// AUDIO
	audioUrl: z.string().optional(),
	audioDuration: z.number().optional(),
	// FILE
	fileUrl: z.string().optional(),
	fileName: z.string().optional(),
	// LOCATION
	locationTitle: z.string().optional(),
	locationAddress: z.string().optional(),
	locationLatitude: z.number().optional(),
	locationLongitude: z.number().optional(),
	// FLEX
	flexAltText: z.string().optional(),
	flexContents: z.record(z.unknown()).optional(),
})

export type MessageContentInput = z.infer<typeof messageContentSchema>

// Validation refinement for message content based on type
export const validateMessageContent = (
	message: MessageContentInput,
): boolean => {
	switch (message.messageType) {
		case ChatMessageType.TEXT:
			return !!message.text?.trim()
		case ChatMessageType.STICKER:
			return !!message.stickerPackageId && !!message.stickerId
		case ChatMessageType.IMAGE:
			return !!message.imageUrl
		case ChatMessageType.VIDEO:
			return !!message.videoUrl && !!message.videoPreviewUrl
		case ChatMessageType.AUDIO:
			return !!message.audioUrl && message.audioDuration !== undefined
		case ChatMessageType.FILE:
			return !!message.fileUrl && !!message.fileName
		case ChatMessageType.LOCATION:
			return (
				message.locationLatitude !== undefined &&
				message.locationLongitude !== undefined &&
				!!message.locationAddress
			)
		case ChatMessageType.FLEX:
			return !!message.flexAltText && !!message.flexContents
		default:
			return false
	}
}

// ==================== Auto Reply Rule Types ====================

export const autoReplyRuleSchema = z.object({
	id: z.string().optional(),
	keywords: z.array(z.string()),
	priority: z.number().default(0),
	isActive: z.boolean().default(true),
	messages: z.array(messageContentSchema).min(1),
})

export type AutoReplyRuleInput = z.infer<typeof autoReplyRuleSchema>

// ==================== Form Schemas ====================

export const greetingSettingsSchema = z.object({
	greetingEnabled: z.boolean(),
	greetingMessages: z.array(messageContentSchema),
})

export type GreetingSettingsFormData = z.infer<typeof greetingSettingsSchema>

export const autoReplySettingsSchema = z.object({
	autoReplyEnabled: z.boolean(),
	autoReplyRules: z.array(autoReplyRuleSchema),
})

export type AutoReplySettingsFormData = z.infer<typeof autoReplySettingsSchema>

// ==================== Message Type Config ====================

export type MessageTypeConfig = {
	value: ChatMessageType
	label: string
	icon: string
	description: string
}

export const MESSAGE_TYPE_CONFIGS: MessageTypeConfig[] = [
	{
		value: ChatMessageType.TEXT,
		label: 'ข้อความ',
		icon: '💬',
		description: 'ข้อความตัวอักษร',
	},
	{
		value: ChatMessageType.STICKER,
		label: 'สติกเกอร์',
		icon: '😀',
		description: 'สติกเกอร์ LINE',
	},
	{
		value: ChatMessageType.IMAGE,
		label: 'รูปภาพ',
		icon: '🖼️',
		description: 'ไฟล์รูปภาพ',
	},
	{
		value: ChatMessageType.VIDEO,
		label: 'วิดีโอ',
		icon: '🎥',
		description: 'ไฟล์วิดีโอ',
	},
	{
		value: ChatMessageType.AUDIO,
		label: 'เสียง',
		icon: '🔊',
		description: 'ไฟล์เสียง',
	},
	{
		value: ChatMessageType.FILE,
		label: 'ไฟล์',
		icon: '📎',
		description: 'ไฟล์เอกสาร',
	},
	// {
	// 	value: ChatMessageType.LOCATION,
	// 	label: 'ตำแหน่ง',
	// 	icon: '📍',
	// 	description: 'ตำแหน่งที่ตั้ง',
	// },
	{
		value: ChatMessageType.FLEX,
		label: 'Flex Message',
		icon: '📱',
		description: 'ข้อความแบบกำหนดเอง',
	},
]

export const getMessageTypeConfig = (
	type: ChatMessageType,
): MessageTypeConfig | undefined => {
	return MESSAGE_TYPE_CONFIGS.find((config) => config.value === type)
}

// ==================== Constants ====================

export const MAX_MESSAGES_PER_RULE = 5
export const MAX_AUTO_REPLY_RULES = 50
